import { DestroyRef, inject, Injectable } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { IqDatadogLogsService } from '@dat/datiq-observability';
import { addRxPlugin, createRxDatabase, RxDatabase, RxDocument } from 'rxdb';
import { getRxStorageDexie } from 'rxdb/plugins/storage-dexie';
import { from, mergeMap, Observable, startWith } from 'rxjs';
import { RxDBJsonDumpPlugin } from 'rxdb/plugins/json-dump';

addRxPlugin(RxDBJsonDumpPlugin);

@Injectable({
    providedIn: 'root',
})
export class IqFeatureFlagsDbService {

    private readonly _datadogLogs = inject(IqDatadogLogsService);
    private readonly _destroyRef = inject(DestroyRef);
    
    private _db: Promise<RxDatabase> = createRxDatabase({
        name: 'optimizely-experiments',
        storage: getRxStorageDexie(),
        eventReduce: true
    });
    private _collection = this._db.then((db: RxDatabase) => db.addCollections({
        flags: {
            schema: {
                version: 0,
                primaryKey: 'flag',
                type: 'object',
                properties: {
                    flag: {
                        type: 'string',
                        maxLength: 100
                    },
                    ruleKey: {
                        type: 'string'
                    },
                    variationKey: {
                        type: 'string'
                    }
                },
                required: ['ruleKey', 'variationKey'],
                title: 'Optimizely Experiments'
            }
        }
    }));
    
    constructor() {
        this._collection.then(collection => {
            collection.flags.$.pipe(
                startWith(null),
                takeUntilDestroyed(this._destroyRef),
                mergeMap(() => from(collection.flags.exportJSON()))
            ).subscribe(json => {
                this._datadogLogs.info('flags local db state', { custom: { docs: json.docs } });
            });
        });
    }

    hasFlag(flag: string): Observable<boolean> {
        return from(this._collection.then(
            ({ flags }) => flags.findOne({
                selector: {
                    flag
                }
            })
            .exec(true)
            .then(() => true)
            .catch(() => false)
        ));
    }

    getFlag(flag: string): Observable<RxDocument<{ flag: string, ruleKey: string, variationKey: string }>> {
        return from(this._collection.then(
            ({ flags }) => flags.findOne({
                selector: {
                    flag
                }
            })
            .exec(true)
        ));
    }

    upsertFlag(flag: string, ruleKey: string, variationKey: string): Observable<void> {
        return from(this._collection.then(
            ({ flags }) => flags.upsert({
                flag,
                ruleKey,
                variationKey
            })
        ));
    }

    removeFlag(flag: string): Observable<void> {
        return from(this._collection.then(
            ({ flags }) => flags.findOne({
                selector: {
                    flag
                }
            }).exec().then(doc => doc?.remove())
        ));
    }
}
